import { children, createContext, createMemo, createSignal, For, JSXElement, Show, Signal, ValidComponent } from "solid-js"
import { requireContext } from "common";
import { SelectTrigger } from "./fragments/SelectTrigger";
import { SelectGroup } from "./fragments/SelectGroup";
import { SelectItem } from "./fragments/SelectItem";
import { SelectContext, SelectProvider, requireSelectContext } from "./SelectContext";
import { Dynamic } from "solid-js/web";
import Dropdown from '../st-dropdown/st-dropdown';
import styles from './st-select.module.css';

export class SelectProps<T extends string = string> {
    children: JSXElement;
    value!: Signal<T>;
    label?: string;
    icon?: string;
    open?: Signal<boolean>;
    as?: ValidComponent;
    displayValue?: JSXElement
}

export type SelectOption<T> = {
    value: T
    label: string
}



export default function Select<T extends string = string>(props: SelectProps<T>) {
    const ctx = new SelectContext<T>(props.value, props.open ?? createSignal(false));

    return (
        <SelectProvider value={ctx as unknown as SelectContext}>
            <SelectInternal {...props} />
        </SelectProvider>
    );

}

Select.defaultProps = new SelectProps<string>();

// We have to do this because we need the context available here.
function SelectInternal<T extends string = string>(props: SelectProps<T>) {
    const ctx = requireSelectContext<T>();

    return <>
        <Dropdown open={[ctx.open, ctx.setOpen]}>
            {props.children}
            <Show when={!ctx.hasTrigger}>
                <Dropdown.Trigger as="div" class={`${styles['select-trigger']} flexbox horizontal middle`} role='button' tabindex="0">
                    <div class="auto">
                        {props.displayValue ?? ctx.defaultDisplayValue()}
                    </div>
                    <div>
                        <i class={`fa fa-${props.icon ?? 'caret-down'}`} style='padding-right: 0.25em; opacity: 0.7;'></i>
                    </div>
                </Dropdown.Trigger>
            </Show>
            <Dropdown.Content>
                <Dynamic component={props.as ?? 'ul'} class={styles['select-list']}>
                    <For each={ctx.groups()}>{group =>
                        <>
                            <Show when={group.label}>
                                <li class={styles['group-label']}>{group.label}</li>
                            </Show>
                            <For each={group.items}>{it => it}</For>
                        </>
                    }</For>
                </Dynamic>
            </Dropdown.Content>
        </Dropdown>
    </>
}

Select.Trigger = SelectTrigger;
Select.Group = SelectGroup;
Select.Item = SelectItem;
Select.Option = SelectItem;
